import React, {useEffect, useState} from 'react';
import {
  View,
  Text,
  StatusBar,
  ScrollView,
  StyleSheet,
  Platform,
  Pressable,
  Linking,
  ActivityIndicator,
  Image,
} from 'react-native';

import mystyles from '../../assets/styles/mystyles'; // Adjust path if necessary
import {
  callApi,
  formatDate,
  getTimeMs,
  getUserIdEmailPhone,
  handleVerifyUser,
  performFollow,
} from '../Helper/Helper';
import {useNavigation, useRoute} from '@react-navigation/native'; // Adjust path if necessary
import {SafeAreaView} from 'react-native-safe-area-context';
import viewmapImg from '../../image/viewmap.png';

// Reusable component to list services based on endpoint and zipcode
const ServiceListView = ({ endpoint, zipcode, userInfo }) => {
  const navigation = useNavigation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [followBusy, setFollowBusy] = useState({});

  const handleFollow = async (item, rowKey) => {
    await performFollow(item, userInfo?.userId, userInfo?.email, userInfo?.phone, setFollowBusy, rowKey);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Example: GET /eabu-eat/90210
        const json = await callApi('GET', `${endpoint}/${zipcode}`);
        setData(json);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    };

    if (zipcode) {
      fetchData();
    }
  }, [endpoint, zipcode]);

  if (loading) {
    return (
      <View style={{ padding: 20, alignItems: 'center' }}>
        <ActivityIndicator size="large" color="#0A84FF" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={{ padding: 20 }}>
        <Text>Error loading data: {error.message}</Text>
      </View>
    );
  }

  const dataObject = JSON.parse(JSON.stringify(data));
  const items = dataObject?.Items ?? [];

  if (items.length === 0) {
    return (
      <View style={{ padding: 20, alignItems: 'center' }}>
        <Text style={{ color: '#666' }}>No services found in {zipcode}</Text>
      </View>
    );
  }

  // Sort by CreatedAt descending
  const sortedItems = [...items].sort((a, b) => {
    const ta = getTimeMs(a?.CreatedAt ?? a?.createdAt);
    const tb = getTimeMs(b?.CreatedAt ?? b?.createdAt);
    return tb - ta;
  });

  return (
    <ScrollView contentContainerStyle={{ paddingBottom: 100 }}>
      {sortedItems.map((item, idx) => {
        const rowKey = item?.ID ?? item?.itemId ?? item?.pk ?? String(idx);
        return (
          <View
            key={rowKey}
            style={[mystyles.card, idx % 2 === 1 && mystyles.cardAlt]}
          >
            {!!item?.title && (
              <View style={styles.highlightedTitleContainer}>
                <Text style={[mystyles.title, styles.highlightedTitle]}>{item.title}</Text>
              </View>
            )}

            <Text style={styles.offerDetailsLabel}>Offer Details:</Text>

            {!!item?.description && (
              <Text style={mystyles.description}>{item.description}</Text>
            )}

            <Text style={styles.postedText}>
              Posted: {formatDate(item?.CreatedAt ?? item?.createdAt)}
            </Text>

            {(!!item?.phone || !!item?.userId) && (
              <View style={styles.actionsRow}>
                {!!item?.phone && (
                  <>
                    <Pressable
                      onPress={() => Linking.openURL(`tel:${item.phone}`)}
                      style={({pressed}) => [
                        styles.actionPill,
                        pressed && styles.actionPillPressed,
                      ]}
                      accessibilityRole="button"
                      accessibilityLabel="Call"
                      hitSlop={8}
                    >
                      <Text style={styles.actionIcon}>📞</Text>
                      <Text style={styles.actionText}>Call</Text>
                    </Pressable>

                    <Pressable
                      onPress={() => Linking.openURL(`sms:${item.phone}`)}
                      style={({pressed}) => [
                        styles.actionPill,
                        pressed && styles.actionPillPressed,
                      ]}
                      accessibilityRole="button"
                      accessibilityLabel="Text message"
                      hitSlop={8}
                    >
                      <Text style={styles.actionIcon}>💬</Text>
                      <Text style={styles.actionText}>Text</Text>
                    </Pressable>
                  </>
                )}

                {!!item?.userId && (
                  <Pressable
                    onPress={() =>
                      navigation.navigate('ProviderDetails', {
                        pUserId: item.userId,
                      })
                    }
                    style={({pressed}) => [
                      styles.actionPill,
                      pressed && styles.actionPillPressed,
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel="View profile"
                    hitSlop={8}
                  >
                    <Text style={styles.actionIcon}>😊</Text>
                    <Text style={styles.actionText}>Profile</Text>
                  </Pressable>
                )}

                {!!item?.userId && (
                  <Pressable
                    onPress={() => handleVerifyUser(item.userId)}
                    style={({pressed}) => [
                      styles.actionPill,
                      pressed && styles.actionPillPressed,
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel="View on map"
                    hitSlop={8}
                  >
                    <Image
                      source={viewmapImg}
                      style={styles.mapIcon}
                    />
                    <Text style={styles.actionText}>Map</Text>
                  </Pressable>
                )}

                {!!item?.userId && (
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => handleFollow(item, rowKey)}
                    disabled={!!followBusy[rowKey]}
                    style={({pressed}) => [
                      styles.followPill,
                      !!followBusy[rowKey] && styles.followPillDisabled,
                      pressed && !followBusy[rowKey] && styles.followPillPressed,
                    ]}
                    hitSlop={8}
                  >
                    <Text style={styles.followText}>
                      {followBusy[rowKey] ? 'Following…' : 'Follow me'}
                    </Text>
                  </Pressable>
                )}

              </View>
            )}
          </View>
        );
      })}
    </ScrollView>
  );
};

const AllServices = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const info = await getUserIdEmailPhone();
        setUserInfo(info);
      } catch (e) {
        console.warn('Failed to get user info', e);
      }
    })();
  }, []);

  // Get zipcode from navigation params, default to empty string if missing
  const { zipcode } = route.params || {};

  // Tabs: 'food', 'household', 'transport'
  const [activeTab, setActiveTab] = useState('food');

  if (!zipcode) {
    return (
      <SafeAreaView style={mystyles.safe}>
        <View style={mystyles.content}>
          <Text>No zipcode provided. Please go back and enter a zipcode.</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={mystyles.safe}>
      <StatusBar
        barStyle={Platform.OS === 'ios' ? 'light-content' : 'light-content'}
        backgroundColor="#0A84FF"
      />

      <View style={styles.headerContainer}>
        <Text style={styles.headerTitle}>Services in {zipcode}</Text>
      </View>

      <View style={styles.tabContainer}>
        <Pressable
          onPress={() => setActiveTab('food')}
          style={[styles.tabButton, activeTab === 'food' && styles.tabButtonActive]}
        >
          <Text style={[styles.tabText, activeTab === 'food' && styles.tabTextActive]}>
            Food Service
          </Text>
        </Pressable>

        <Pressable
          onPress={() => setActiveTab('household')}
          style={[styles.tabButton, activeTab === 'household' && styles.tabButtonActive]}
        >
          <Text style={[styles.tabText, activeTab === 'household' && styles.tabTextActive]}>
            Household
          </Text>
        </Pressable>

        <Pressable
          onPress={() => setActiveTab('transport')}
          style={[styles.tabButton, activeTab === 'transport' && styles.tabButtonActive]}
        >
          <Text style={[styles.tabText, activeTab === 'transport' && styles.tabTextActive]}>
            Moving
          </Text>
        </Pressable>
      </View>

      <View style={mystyles.content}>
        {activeTab === 'food' && (
          <ServiceListView endpoint="/eabu-eat" zipcode={zipcode} userInfo={userInfo} />
        )}
        {activeTab === 'household' && (
          <ServiceListView endpoint="/eabu-live" zipcode={zipcode} userInfo={userInfo} />
        )}
        {activeTab === 'transport' && (
          <ServiceListView endpoint="/eabu-move" zipcode={zipcode} userInfo={userInfo} />
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  headerContainer: {
    padding: 15,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    alignItems: 'center'
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  tabContainer: {
    flexDirection: 'row',
    marginHorizontal: 10,
    marginTop: 10,
    marginBottom: 10,
    backgroundColor: '#f1f3f5',
    borderRadius: 30,
    padding: 4,
  },
  tabButton: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 25,
    justifyContent: 'center',
  },
  tabButtonActive: {
    backgroundColor: '#ffffff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  tabText: {
    fontWeight: '600',
    color: '#868e96',
    fontSize: 13, // Slightly smaller to fit 3 tabs
  },
  tabTextActive: {
    color: '#343a40',
    fontWeight: '700',
  },
  highlightedTitleContainer: {
    backgroundColor: '#F0F8FF',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  highlightedTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#007AFF',
    textAlign: 'center',
  },
  offerDetailsLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: 10,
    marginBottom: 5,
    color: '#333',
  },
  postedText: {
    fontSize: 12,
    color: '#666',
  },
  actionsRow: {
    marginTop: 12,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 10,
  },
  actionPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  actionPillPressed: {
    opacity: 0.75,
    transform: [{ scale: 0.99 }],
  },
  actionIcon: {
    fontSize: 16,
  },
  actionText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#0F172A',
  },
  mapIcon: {
    width: 18,
    height: 18,
    resizeMode: 'contain',
  },
  followPill: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 999,
    backgroundColor: '#0A84FF',
    borderWidth: 1,
    borderColor: 'rgba(10,132,255,0.35)',
  },
  followPillPressed: {
    opacity: 0.85,
    transform: [{ scale: 0.99 }],
  },
  followPillDisabled: {
    opacity: 0.55,
  },
  followText: {
    fontSize: 12,
    fontWeight: '800',
    color: '#FFFFFF',
  },
});

export default AllServices;
